import { omit } from "lodash";
import { HTMLAttributes } from "react";
import styled from "styled-components";
import { Icon, IconType } from "../../icons";
import { Flex } from "../../layout";

export const iconBackgroundSize = 2; // compared to icon
export const iconSize = 1.5;
export const barSize = 1.3;
export const iconLeftPadding = 1;
export const iconBackgroundRatio = 0.65; // compared to bar

const variants = ["main", "sub", "leaf"] as const;
export type Variant = typeof variants[number];

export type ItemProps = HTMLAttributes<HTMLElement> & {
  icon?: React.ReactNode;
  chevron?: boolean;
  chevronState?: ChevronState;
  tab?: number;
  compact?: boolean;
};

export type CustomItemProps = Omit<ItemProps, "icon"> & {
  icon?: IconType;
};

export type ChevronState = "expanded" | "collapsed";

const IconWrapper = styled(Flex).attrs({ center: true })`
  display: flex;
  width: ${iconBackgroundSize}em;
  height: ${iconBackgroundSize}em;
  font-size: ${iconSize}em;
  align-self: center;
`;

export const IconContainer = styled(Flex).attrs({ center: true })`
  height: ${iconBackgroundSize * iconBackgroundRatio}em;
  width: ${iconBackgroundSize * iconBackgroundRatio}em;
  border-radius: 7px;
`;

const Container = styled.div<
  Pick<ItemProps, "tab" | "chevron" | "compact"> & { hasPointer: boolean }
>`
  display: flex;
  width: 100%;
  height: ${iconBackgroundSize * iconSize * barSize}em;
  align-items: stretch;
  padding-left: ${iconLeftPadding}em;

  ${(props) =>
    props.tab && !props.compact
      ? `padding-left: ${1 + 1.5 * props.tab}em;`
      : ""}
  ${(props) => (props.hasPointer ? "cursor: pointer" : "")};
`;

const Children = styled(Flex)`
  flex-grow: 1;
  &:last-child {
    padding-right: 2em;
  }
  &:first-child {
    padding-left: 1em;
  }
`;

export const Item: React.FunctionComponent<ItemProps> = (props) => {
  const {
    icon,
    chevron,
    chevronState = "collapsed",
    children,
    compact,
    onClick,
  } = props;
  const passedProps = omit(props, "icon", "children");

  return (
    <Container {...passedProps} hasPointer={onClick !== undefined}>
      {icon ? <IconWrapper>{icon ?? icon}</IconWrapper> : null}
      <Children>{compact ? null : children}</Children>
      {chevron ? (
        <IconWrapper>
          <Icon
            type="arrowDown"
            scale={0.5}
            rotateAmount={chevronState === "collapsed" ? 0 : 180}
          />
        </IconWrapper>
      ) : null}
    </Container>
  );
};
